// src/pages/admin/CreateEvent.js - COMPLETE WITH EDIT SUPPORT
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    Grid,
    Alert,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { Save as SaveIcon, ArrowBack as BackIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { createEvent, getEventById, updateEvent } from "../../services/eventService";
import AdminLayout from "../AdminLayout";

export default function CreateEvent() {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const isEditMode = Boolean(eventId);

    const [loading, setLoading] = useState(false);
    const [fetchingEvent, setFetchingEvent] = useState(isEditMode);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        eventDate: "",
        eventTime: "",
        venue: "",
        registrationDeadline: "",
        maxCapacity: "",
        hasCapacityLimit: false,
        eventType: "Workshop",
        organizer: "",
        contactEmail: "",
        contactPhone: "",
    });

    useEffect(() => {
        if (isEditMode) {
            fetchEventData();
        }
    }, [eventId]);

    const fetchEventData = async () => {
        try {
            setFetchingEvent(true);
            const event = await getEventById(eventId);
            setFormData({
                title: event.title || "",
                description: event.description || "",
                eventDate: event.eventDate || "",
                eventTime: event.eventTime || "",
                venue: event.venue || "",
                registrationDeadline: event.registrationDeadline || "",
                maxCapacity: event.maxCapacity || "",
                hasCapacityLimit: Boolean(event.maxCapacity),
                eventType: event.eventType || "Workshop",
                organizer: event.organizer || "",
                contactEmail: event.contactEmail || "",
                contactPhone: event.contactPhone || "",
            });
        } catch (err) {
            setError("Failed to load event data");
        } finally {
            setFetchingEvent(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        // Validation
        if (!formData.title || !formData.eventDate || !formData.registrationDeadline) {
            setError("Please fill in all required fields");
            return;
        }

        if (new Date(formData.registrationDeadline) >= new Date(formData.eventDate)) {
            setError("Registration deadline must be before event date");
            return;
        }

        try {
            setLoading(true);
            const dataToSubmit = {
                ...formData,
                maxCapacity: formData.hasCapacityLimit
                    ? parseInt(formData.maxCapacity)
                    : null,
            };

            if (isEditMode) {
                await updateEvent(eventId, dataToSubmit);
                setSuccess("Event updated successfully!");
            } else {
                await createEvent(dataToSubmit);
                setSuccess("Event created successfully!");
            }

            setTimeout(() => navigate("/admin/events"), 2000);
        } catch (err) {
            setError(`Failed to ${isEditMode ? "update" : "create"} event. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    if (fetchingEvent) {
        return (
            <AdminLayout title={isEditMode ? "Edit Event" : "Create Event"}>
                <Typography>Loading event data...</Typography>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title={isEditMode ? "Edit Event" : "Create Event"}>
            <Box>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate("/admin/events")}
                    sx={{ mb: 3, color: "#003366" }}
                >
                    Back to Events
                </Button>

                <Card>
                    <CardContent>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 600, color: "#003366", mb: 3 }}
                        >
                            {isEditMode ? "Edit Event" : "Create New Event"}
                        </Typography>

                        {error && (
                            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        {success && (
                            <Alert
                                severity="success"
                                onClose={() => setSuccess("")}
                                sx={{ mb: 2 }}
                            >
                                {success}
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                {/* Basic Information */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 600, color: "#C1272D", mb: 2 }}
                                    >
                                        Basic Information
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Event Title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Event Type"
                                        name="eventType"
                                        value={formData.eventType}
                                        onChange={handleChange}
                                        placeholder="e.g., Workshop, Seminar, Conference"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                {/* Date & Time */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 600, color: "#C1272D", mb: 2, mt: 2 }}
                                    >
                                        Date & Time Details
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="date"
                                        label="Event Date"
                                        name="eventDate"
                                        value={formData.eventDate}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="time"
                                        label="Event Time"
                                        name="eventTime"
                                        value={formData.eventTime}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        type="date"
                                        label="Registration Deadline"
                                        name="registrationDeadline"
                                        value={formData.registrationDeadline}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                {/* Venue & Capacity */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 600, color: "#C1272D", mb: 2, mt: 2 }}
                                    >
                                        Venue & Capacity
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Venue"
                                        name="venue"
                                        value={formData.venue}
                                        onChange={handleChange}
                                        placeholder="e.g., Auditorium, Room 101"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.hasCapacityLimit}
                                                onChange={handleChange}
                                                name="hasCapacityLimit"
                                                color="primary"
                                            />
                                        }
                                        label="Set Maximum Capacity"
                                    />
                                    {formData.hasCapacityLimit && (
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Maximum Capacity"
                                            name="maxCapacity"
                                            value={formData.maxCapacity}
                                            onChange={handleChange}
                                            sx={{ mt: 2 }}
                                        />
                                    )}
                                </Grid>

                                {/* Contact Information */}
                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 600, color: "#C1272D", mb: 2, mt: 2 }}
                                    >
                                        Contact Information
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Organizer Name"
                                        name="organizer"
                                        value={formData.organizer}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        type="email"
                                        label="Contact Email"
                                        name="contactEmail"
                                        value={formData.contactEmail}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Contact Phone"
                                        name="contactPhone"
                                        value={formData.contactPhone}
                                        onChange={handleChange}
                                    />
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 2,
                                            justifyContent: "flex-end",
                                            mt: 2,
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate("/admin/events")}
                                            sx={{
                                                color: "#003366",
                                                borderColor: "#003366",
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            disabled={loading}
                                            sx={{
                                                backgroundColor: "#C1272D",
                                                "&:hover": { backgroundColor: "#a01f25" },
                                            }}
                                        >
                                            {loading
                                                ? (isEditMode ? "Updating..." : "Creating...")
                                                : (isEditMode ? "Update Event" : "Create Event")
                                            }
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                    </CardContent>
                </Card>
            </Box>
        </AdminLayout>
    );
}
